import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { loadSfxAssets, invalidateSfxCache, playSfxAsset } from "../../lib/soundBus";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Play, X, Upload } from "lucide-react";

export default function SoundPicker({ gameId, value, onChange, label, testId }) {
  const [assets, setAssets] = useState([]);
  const fileRef = useRef(null);
  const previewClickRef = useRef(false);

  useEffect(() => {
    loadSfxAssets(gameId).then(setAssets);
  }, [gameId]);

  const handleSelect = (v) => {
    if (v === "__upload") {
      fileRef.current?.click();
      return;
    }
    onChange(v === "__none" ? null : v);
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    const { data } = await api.post(`/uploads?game_id=${gameId}&kind=sfx`, formData, { headers: { "Content-Type": "multipart/form-data" } });
    invalidateSfxCache(gameId);
    const updated = await loadSfxAssets(gameId, true);
    setAssets(updated);
    onChange(data.id);
  };

  return (
    <div className="flex items-center gap-1.5" data-testid={testId}>
      <span className="text-[10px] uppercase text-[#f1e0c5]/40 w-14 shrink-0">{label}</span>
      <Select value={value || "__none"} onValueChange={handleSelect}>
        <SelectTrigger className="h-7 bg-black/30 border-white/10 text-xs"><SelectValue placeholder="None" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__none">None (use GM default)</SelectItem>
          {assets.map((a) => (
            <SelectItem
              key={a.id}
              value={a.id}
              onSelect={(e) => {
                // Radix fires this for ANY click inside the item, including our nested preview
                // button — only swallow it when the click actually originated from that button.
                if (previewClickRef.current) {
                  e.preventDefault();
                  previewClickRef.current = false;
                }
              }}
            >
              <span className="flex items-center gap-2 pr-2">
                <button
                  type="button"
                  data-testid={`sound-preview-${a.id}`}
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    previewClickRef.current = true;
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    playSfxAsset(a.id);
                  }}
                  className="text-[#D4AF37] hover:text-white shrink-0"
                >
                  <Play className="w-3 h-3" />
                </button>
                {a.original_filename}
              </span>
            </SelectItem>
          ))}
          <SelectItem value="__upload">
            <span className="flex items-center gap-1.5"><Upload className="w-3 h-3" /> Upload new…</span>
          </SelectItem>
        </SelectContent>
      </Select>
      {value && (
        <button onClick={() => playSfxAsset(value)} className="text-[#D4AF37] hover:text-white shrink-0"><Play className="w-3.5 h-3.5" /></button>
      )}
      <input ref={fileRef} type="file" accept="audio/*" className="hidden" onChange={handleUpload} />
    </div>
  );
}
